import { useEffect, useMemo, useRef, useState } from "react";
import { limit, orderBy, serverTimestamp } from "firebase/firestore";
import { useFirebase } from "../../wrappers/firebase";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";
import getMillis from "../../helpers/getMillis";

/**
 * Messages live under: profiles/{stateUserId}/messages
 * Fields: body, createdOn, isRead, readOn, senderId, recipientId, messageType
 * Messages are not created client-side; they can only be updated (e.g., mark as read).
 * No caching via react-query – keep ephemeral state only.
 */
function useMessages() {
  const { getData, subscribe, updateDocument } = useFirebase();
  const { id: stateUserId } =
    useUserStore((state) => state.user, shallow) || {};

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);

  const collectionPath = `profiles/${stateUserId}/messages`;

  useEffect(() => {
    if (!stateUserId) return;

    const unsubscribe = subscribe({
      path: collectionPath,
      observerOnlyChanges: true,
      filters: [orderBy("createdAt", "desc"), limit(10)],
      callback: (data) => {
        setMessages((prev) => {
          const map = new Map();
          // Add previous messages first
          prev.forEach((m) => map.set(m.id, m));
          // Overwrite/add with new data
          data.forEach((m) => map.set(m.id, m));

          return Array.from(map.values()).sort((a, b) => {
            // Handle null/undefined timestamps gracefully
            const aTime = getMillis(a.createdAt);
            const bTime = getMillis(b.createdAt);
            return bTime - aTime;
          });
        });
      },
    });

    return () => {
      unsubscribe();
    };
  }, [stateUserId]);

  const updateMessage = async ({ messageId, data }) => {
    const path = `${collectionPath}/${messageId}`;
    await updateDocument({ path, data });
  };

  const markAsRead = async (messageId) => {
    return updateMessage({
      messageId,
      data: { isRead: true, readOn: serverTimestamp() },
    });
  };

  const markAllAsRead = async () => {
    const unreadMessages = messages.filter((m) => !m.isRead);
    await Promise.all(unreadMessages.map((m) => markAsRead(m.id)));
  };

  const getUnreadCount = () => {
    return messages.filter((m) => !m.isRead).length;
  };

  return {
    messages,
    isLoading,
    error,
    updateMessage,
    markAsRead,
    markAllAsRead,
    getUnreadCount,
  };
}

export default useMessages;
